import { onlineManager } from "@tanstack/react-query";
import { create } from "zustand";

export const BACKEND_RECONNECT_INTERVAL_MS = 60_000;
export const BACKEND_HEALTH_PROBE_PATH = "/api/hub/v1/public/ping";
export const BACKEND_UNAVAILABLE_ID = "backend_unreachable";

type BackendHealthStatus = "online" | "offline";

interface BackendHealthState {
  status: BackendHealthStatus;
  isChecking: boolean;
  nextRetryAt: number | null;
  lastOfflineAt: number | null;
  lastError: string | null;
  reportBackendFailure: (message?: string) => void;
  reportBackendSuccess: () => void;
  beginBackendCheck: () => void;
  finishBackendCheck: (ok: boolean, message?: string) => void;
}

interface BackendErrorLike {
  id?: unknown;
  message?: unknown;
  status?: unknown;
  statusCode?: unknown;
}

export function isBackendUnavailableMessage(message: unknown): boolean {
  if (typeof message !== "string") return false;
  return /cannot connect to backend|server unreachable|failed to fetch|fetch failed|networkerror/i.test(
    message,
  );
}

export function isBackendUnavailableError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const candidate = error as BackendErrorLike;
  if (candidate.id === BACKEND_UNAVAILABLE_ID) return true;

  const status = candidate.statusCode ?? candidate.status;
  if (status === 0 && isBackendUnavailableMessage(candidate.message)) return true;
  if (status === 502 && isBackendUnavailableMessage(candidate.message)) return true;
  return error instanceof TypeError && isBackendUnavailableMessage(error.message);
}

function nextRetryAt(now = Date.now()): number {
  return now + BACKEND_RECONNECT_INTERVAL_MS;
}

export const useBackendHealthStore = create<BackendHealthState>((set) => ({
  status: "online",
  isChecking: false,
  nextRetryAt: null,
  lastOfflineAt: null,
  lastError: null,

  reportBackendFailure: (message = "Cannot connect to backend") => {
    onlineManager.setOnline(false);
    const now = Date.now();
    set((state) => ({
      status: "offline",
      isChecking: false,
      nextRetryAt:
        state.status === "offline" && state.nextRetryAt && state.nextRetryAt > now
          ? state.nextRetryAt
          : nextRetryAt(now),
      lastOfflineAt: state.lastOfflineAt ?? now,
      lastError: message,
    }));
  },

  reportBackendSuccess: () => {
    onlineManager.setOnline(true);
    set({
      status: "online",
      isChecking: false,
      nextRetryAt: null,
      lastOfflineAt: null,
      lastError: null,
    });
  },

  beginBackendCheck: () => set({ isChecking: true }),

  finishBackendCheck: (ok, message = "Cannot connect to backend") => {
    if (ok) {
      onlineManager.setOnline(true);
      set({
        status: "online",
        isChecking: false,
        nextRetryAt: null,
        lastOfflineAt: null,
        lastError: null,
      });
      return;
    }

    onlineManager.setOnline(false);
    const now = Date.now();
    set((state) => ({
      status: "offline",
      isChecking: false,
      nextRetryAt: nextRetryAt(now),
      lastOfflineAt: state.lastOfflineAt ?? now,
      lastError: message,
    }));
  },
}));

export const useIsBackendOffline = () =>
  useBackendHealthStore((state) => state.status === "offline");
